import {
  Card,
  CircularProgress,
  drawerClasses,
  Grid,
  MenuItem,
  TextField,
  Theme,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import {
  ActionStatus,
  GameBehavior,
  MahjongGameBehaviorTrendChartData,
} from "../types/GameBehaviorLog";
import MahjongGameBehaviorChart from "./MahjongGameBehaviorChart";
import { Month, months } from "./PlaytimeTab";
import { chown } from "fs/promises";
import YearMenuItems from "./YearMenuItems";

const StyledVerticalTabContainer = styled.div`
  // flex-grow: 1;
  // display: flex;
`;
const StyledTabPanel = styled.div`
  width: 100%;
`;

interface MahjongGameBehaviorChartTabsProps {
  data: GameBehavior[] | undefined;
  mode: 0 | 1;
}

function a11yProps(index: any) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return <StyledTabPanel hidden={value !== index}>{children}</StyledTabPanel>;
}

function usePrevious(value: any) {
  const ref = useRef(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export default function MahjongGameBehaviorChartTabs(
  props: MahjongGameBehaviorChartTabsProps
) {
  const { t } = useTranslation();

  const translationMonth = {
    1: t("months.1"),
    2: t("months.2"),
    3: t("months.3"),
    4: t("months.4"),
    5: t("months.5"),
    6: t("months.6"),
    7: t("months.7"),
    8: t("months.8"),
    9: t("months.9"),
    10: t("months.10"),
    11: t("months.11"),
    12: t("months.12"),
  };

  const [chartData, setChartData] =
    useState<MahjongGameBehaviorTrendChartData>();

  const [loading, setLoading] = useState<boolean>(true);

  const [timeUnit, setTimeUnit] = useState<
    { year: number; month: number } | undefined
  >({ year: moment().year(), month: moment().month() });

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    if (
      props.data !== undefined &&
      props.mode !== undefined &&
      timeUnit !== undefined
    ) {
      setChartData(extractDataByTime(generateChartData(props.data ?? [])));
    }
  }, [props.data, props.mode, timeUnit]);

  const generateChartData = (
    data: GameBehavior[]
  ): MahjongGameBehaviorTrendChartData => {
    // Group by time and calculate average at day interval

    let dataGroupedByTime = _(data)
      .map((entry) => {
        return {
          ...entry,
          day: moment(entry.timestamp).startOf("day").valueOf(),
        };
      })
      .groupBy((entry) => entry.day)
      .valueOf();

    for (let items in dataGroupedByTime) {
      dataGroupedByTime[items] = dataGroupedByTime[items].filter((obj) =>
        Object.keys(obj).includes("behaviors")
      );
    }

    const dataReducer = Object.entries(dataGroupedByTime).reduce<{
      [key: string]: {};
    }>((buffer, [dayTimestamp, logs]) => {
      const allBehaviorFromCurrentPlayer = _(logs)
        .map((log) => log.behaviors)
        .flatten()
        .valueOf();
      const behaviorGroupedByActionType = _(allBehaviorFromCurrentPlayer)
        .groupBy((behavior) => behavior.type)
        .valueOf() as { [key: string]: ActionStatus[] };
      const dayAverageValue: {
        [key: string]: { reactionTime: number; wrongRate: number };
      } = Object.entries(behaviorGroupedByActionType).reduce(
        (dayBuffer, [actionType, actions]) => {
          const currentActionReactionTimeAverage = _(actions)
            .filter((action) => action.time !== undefined)
            .meanBy((action) => action.time ?? 0);
          // console.log(_(actions))
          const currentActionWrongRateAverage = _(actions)
            .filter(
              (action) =>
                action.status === "success" || action.status === "fail"
            )
            .meanBy((action) => (action.status === "success" ? 0 : 1));
          return {
            ...dayBuffer,
            [actionType]: {
              reactionTime:
                Math.round(currentActionReactionTimeAverage * 100) / 100,
              wrongRate:
                Math.round(currentActionWrongRateAverage * 10000) / 100,
            },
          };
        },
        {}
      );
      return { ...buffer, [dayTimestamp]: dayAverageValue };
    }, {});

    // dataReducer here may have other use

    // Transform the object from {time: {[action: string]: {reactionTime: number, wrongRate: number}}} to {[action: string]: {[time: number]: {reactionTime: number, wrongRate: number}}}
    const transformedChartData = Object.entries(dataReducer).reduce<{
      [key: string]: {
        [key: string]: { reactionTime: number; wrongRate: number };
      };
    }>(
      (
        buffer,
        [dayTimestamp, actionAvgData]: [
          string,
          { [key: string]: { reactionTime: number; wrongRate: number } }
        ]
      ) => {
        Object.entries(actionAvgData).forEach(
          ([actionType, avgData]: [
            string,
            { reactionTime: number; wrongRate: number }
          ]) => {
            buffer[actionType] || (buffer[actionType] = {});
            buffer[actionType][dayTimestamp] = avgData;
          }
        );

        return buffer;
      },
      {}
    );

    return transformedChartData;
  };

  const extractDataByTime = (data: MahjongGameBehaviorTrendChartData) => {
    if (!timeUnit) return;

    const startOfMonth = moment(
      timeUnit!.year + "-" + (timeUnit!.month !== -1 ? timeUnit!.month : 1)
    )
      .startOf("month")
      .valueOf();
    const endOfMonth = moment(
      timeUnit!.year + "-" + (timeUnit!.month !== -1 ? timeUnit!.month : 12)
    )
      .endOf("month")
      .valueOf();

    const timeFilterData = Object.entries(
      data
    ).reduce<MahjongGameBehaviorTrendChartData>(
      (buffer, [actionType, actionAvgData]) => {
        buffer[actionType] = Object.entries(actionAvgData)
          .filter(
            ([timestamp, avgData]) =>
              parseInt(timestamp) < endOfMonth &&
              parseInt(timestamp) > startOfMonth
          )
          .reduce<{
            [key: string]: { reactionTime: number; wrongRate: number };
          }>((actionBuffer, [timestamp, avgData]) => {
            actionBuffer[timestamp] = avgData;
            return actionBuffer;
          }, {});

        return buffer;
      },
      {}
    );
    return timeFilterData;
  };

  return (
    <StyledVerticalTabContainer>
      <Card style={{ position: "relative" }}>
        {props.data && props.data.length !== 0 ? (
          <>
            <Grid container>
              <Grid item>
                <TextField
                  id="year"
                  select
                  label={t("chart_units.year")}
                  value={timeUnit?.year}
                  onChange={(e: { target: { value: string } }) =>
                    setTimeUnit({
                      year: parseInt(e.target.value),
                      month: timeUnit!.month,
                    })
                  }
                  margin="normal"
                  style={{
                    width: 200,
                    justifySelf: "flex-start",
                    display: "flex",
                    margin: 40,
                  }}
                >
                  {YearMenuItems()}
                </TextField>
              </Grid>
              <Grid item>
                <TextField
                  id="month"
                  select
                  label={t("chart_units.month")}
                  value={timeUnit?.month}
                  onChange={(e: { target: { value: string } }) =>
                    setTimeUnit({
                      year: timeUnit!.year,
                      month: parseInt(e.target.value) as Month,
                    })
                  }
                  margin="normal"
                  style={{
                    width: 200,
                    justifySelf: "flex-start",
                    display: "flex",
                    margin: 40,
                  }}
                >
                  <MenuItem key={-1} value={-1}>
                    {t("chart_units.full_year")}
                  </MenuItem>
                  {months.map((option) => (
                    <MenuItem key={option} value={option}>
                      {translationMonth[option]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {chartData && (
              <MahjongGameBehaviorChart
                selectedYear={timeUnit?.year}
                selectedMonth={
                  timeUnit?.month === -1 ? undefined : timeUnit?.month
                }
                data={chartData}
                mode={props.mode}
              />
            )}
            {/* <DomainTaskTable data={tabData.filter(x => x.domain === domain)[0].missionScore} historyData={props.data} selectedTab={selectedTab} missionOnClick={missionOnClick} /> */}

            <Backdrop
              style={{
                position: "absolute",
                zIndex: 999,
                background: "rgba(255,255,255,0.3)",
              }}
              open={loading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </>
        ) : (
          <div style={{ minHeight: 300 }}>
            <h3 style={{ marginTop: 175 }}>{t("no__.record")}</h3>
            <p>{t("common.start_a_mahjong")}</p>
          </div>
        )}
      </Card>
    </StyledVerticalTabContainer>
  );
}

//average={allTabData.filter(x => x.mission === domain)}
